import React, { useEffect, useRef, useState } from "react";
import { useSound } from "../../utils/sound/SoundProvider";
import useGameBase from '../../hooks/useGameBase';

const BOARD_WIDTH = 20;
const BOARD_HEIGHT = 20;
const CELL_SIZE = 20; // px

function SnakeGame({ gameState, gamePlayers, user, onGameAction, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const canvasRef = useRef(null);
  const [direction, setDirection] = useState("right");
  const [canvasStyle, setCanvasStyle] = useState({ border: "2px solid #888", background: "#fff", marginBottom: 12 });
  const [showOnscreenControls, setShowOnscreenControls] = useState(false);
  const userId = user.socketId;
  // const mySnake = gameState.snakes?.[userId] || []; // Unused
  const isAlive = gameState.alive?.[userId];
  const isGameOver = gameState.finished;
  const winners = gameState.winners || [];
  const food = gameState.food;
  const scores = gameState.scores || {};

  const sound = useSound();
  const prevFoodRef = useRef(null);

  
  // Detect dark mode and update canvas style
  useEffect(() => {
    if (user && user.darkMode) {
      setCanvasStyle({ border: "2px solid #27272a", background: "#18181b", marginBottom: 12 });
    } else {
      setCanvasStyle({ border: "2px solid #eeeeee", background: "#ffffff", marginBottom: 12 });
    }
  }, [user]);

  // Draw the board, snakes, and food
  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    if (user && user.darkMode) {
      ctx.fillStyle = "#18181b"; // dark gray
    } else {
      ctx.fillStyle = "#ffffff"; // white
    }
    ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);
    ctx.clearRect(0, 0, 0, 0); // no-op, just to keep structure
    // Draw food as a black box with a white mouse icon
    if (food) {
      ctx.fillStyle = "#000";
      ctx.fillRect(food.x * CELL_SIZE, food.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
      ctx.save();
      ctx.font = '18px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#fff';
      ctx.fillText('🐭', food.x * CELL_SIZE + CELL_SIZE / 2, food.y * CELL_SIZE + CELL_SIZE / 2 + 1);
      ctx.restore();
    }
    // Draw all snakes with username letters in each segment
    Object.entries(gameState.snakes || {}).forEach(([pid, snake]) => {
      // Find the player for this pid
      const player = gamePlayers.find(p => p.socketId === pid);
      const username = player ? player.username : "";
      // Use as many letters as there are segments, or truncate username
      const letters = username.slice(0, snake.length).split("");
      // Use player color or fallback
      const baseColor = player && player.color ? player.color : (pid === userId ? "#22c55e" : "#3b82f6");
      // Function to lighten a hex color
      function lighten(hex, amt = 0.2) {
        // hex: #RRGGBB
        let c = hex.replace('#', '');
        if (c.length === 3) c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2];
        let num = parseInt(c, 16);
        let r = Math.min(255, Math.floor(((num >> 16) & 0xFF) + (255 - ((num >> 16) & 0xFF)) * amt));
        let g = Math.min(255, Math.floor(((num >> 8) & 0xFF) + (255 - ((num >> 8) & 0xFF)) * amt));
        let b = Math.min(255, Math.floor((num & 0xFF) + (255 - (num & 0xFF)) * amt));
        return `rgb(${r},${g},${b})`;
      }
      snake.forEach((seg, idx) => {
        ctx.globalAlpha = idx === 0 ? 1 : 0.8;
        ctx.fillStyle = idx === 0 ? baseColor : lighten(baseColor, 0.2);
        ctx.fillRect(seg.x * CELL_SIZE, seg.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        // Draw letter if available
        if (letters[idx]) {
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.fillStyle = '#fff';
          ctx.font = 'bold 13px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(letters[idx], seg.x * CELL_SIZE + CELL_SIZE / 2, seg.y * CELL_SIZE + CELL_SIZE / 2 + 1);
          ctx.restore();
        }
      });
      ctx.globalAlpha = 1;
    });
    // Draw grid (dark mode)
    ctx.strokeStyle = "#27272a"; // dark grid
    if (user && user.darkMode) {
      ctx.strokeStyle = "#27272a"; // dark gray
    } else {
      ctx.strokeStyle = "#cccccc"; // white
    }
    ctx.lineWidth = 0.5;
    for (let x = 0; x <= BOARD_WIDTH; x++) {
      ctx.beginPath();
      ctx.moveTo(x * CELL_SIZE, 0);
      ctx.lineTo(x * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);
      ctx.stroke();
    }
    for (let y = 0; y <= BOARD_HEIGHT; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL_SIZE);
      ctx.lineTo(BOARD_WIDTH * CELL_SIZE, y * CELL_SIZE);
      ctx.stroke();
    }

  }, [gameState, userId, food, gamePlayers, user]);

  // Play sound when food (mouse) position changes -> interpreted as 'eat' event
  useEffect(() => {
    try {
      const prev = prevFoodRef.current;
      if (prev && food && (prev.x !== food.x || prev.y !== food.y)) {
        try { sound && sound.play && sound.play('snake:eat'); } catch (e) {}
      }
      // update ref (store null when no food)
      prevFoodRef.current = food ? { x: food.x, y: food.y } : null;
    } catch (e) {}
  }, [food, sound]);


  // Handle keyboard input
  useEffect(() => {
    function handleKey(e) {
      if (!isAlive || isGameOver) return;
      let dir = null;
      if (e.key === "ArrowUp") dir = "up";
      else if (e.key === "ArrowDown") dir = "down";
      else if (e.key === "ArrowLeft") dir = "left";
      else if (e.key === "ArrowRight") dir = "right";
      // Don't interfere with typing in inputs/textareas or contentEditable elements
      const active = document && document.activeElement;
      const tag = active && active.tagName ? active.tagName.toUpperCase() : null;
      const isEditable =
        (active && active.isContentEditable) ||
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (active && active.getAttribute && active.getAttribute("role") === "textbox");
      if (dir && !isEditable) {
        // prevent the page from scrolling when arrow keys are used for game control
        try {
          e.preventDefault();
        } catch (er) {}
      }
      if (dir && dir !== direction) {
    setDirection(dir);
    submitAction({ type: "move", dir }).catch(()=>{});
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [direction, isAlive, isGameOver, submitAction]);

  // Touch controls for mobile
  const handleDirectionBtn = (dir) => {
    if (!isAlive || isGameOver) return;
    if (dir && dir !== direction) {
      setDirection(dir);
      submitAction({ type: "move", dir }).catch(()=>{});
    }
  };

  // Detect whether to show on-screen controls: show when device likely has no physical keyboard
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const hasFinePointer = window.matchMedia && window.matchMedia("(pointer: fine)").matches;
      const prefersHover = window.matchMedia && window.matchMedia("(hover: hover)").matches;
      const hasPhysicalKeyboard = hasFinePointer || prefersHover;
      const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || (window.matchMedia && window.matchMedia('(hover: none)').matches);
      // Show controls if touch device and we don't detect a physical keyboard
      setShowOnscreenControls(!!(isTouchDevice && !hasPhysicalKeyboard));
    } catch (e) {
      // fallback: show controls on touch-capable devices
      setShowOnscreenControls(!!(('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)));
    }
  }, []);

  return (
    <div className="flex flex-col items-center w-full bg-gray-50 dark:bg-gray-900">
      <canvas
        ref={canvasRef}
        width={BOARD_WIDTH * CELL_SIZE}
        height={BOARD_HEIGHT * CELL_SIZE}
        style={canvasStyle}
      />

      {/* On-screen direction controls for mobile/touch (hidden on devices with a keyboard) */}
      {showOnscreenControls && (
        <div className="my-2 flex flex-col items-center select-none" style={{ touchAction: 'none' }}>
        <div className="flex flex-row justify-center mb-1">
          <button
            aria-label="Up"
            className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold m-1 shadow border border-gray-300 dark:border-gray-600 text-black dark:text-white transition-colors duration-100 hover:bg-yellow-100 dark:hover:bg-yellow-900 active:bg-yellow-300 dark:active:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            onClick={() => handleDirectionBtn("up")}
            tabIndex={0}
          >
            {/* Up arrow SVG */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <path d="M5 16l7-8 7 8" />
            </svg>
          </button>
        </div>
        <div className="flex flex-row justify-center">
          <button
            aria-label="Left"
            className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold m-1 shadow border border-gray-300 dark:border-gray-600 text-black dark:text-white transition-colors duration-100 hover:bg-yellow-100 dark:hover:bg-yellow-900 active:bg-yellow-300 dark:active:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            onClick={() => handleDirectionBtn("left")}
            tabIndex={0}
          >
            {/* Left arrow SVG */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <path d="M16 5l-8 7 8 7" />
            </svg>
          </button>
          <div className="w-12 h-12 m-1" />
          <button
            aria-label="Right"
            className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold m-1 shadow border border-gray-300 dark:border-gray-600 text-black dark:text-white transition-colors duration-100 hover:bg-yellow-100 dark:hover:bg-yellow-900 active:bg-yellow-300 dark:active:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            onClick={() => handleDirectionBtn("right")}
            tabIndex={0}
          >
            {/* Right arrow SVG for up button */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <path d="M8 5l8 7-8 7" />
            </svg>
          </button>
        </div>
        <div className="flex flex-row justify-center mt-1">
          <button
            aria-label="Down"
            className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold m-1 shadow border border-gray-300 dark:border-gray-600 text-black dark:text-white transition-colors duration-100 hover:bg-yellow-100 dark:hover:bg-yellow-900 active:bg-yellow-300 dark:active:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            onClick={() => handleDirectionBtn("down")}
            tabIndex={0}
          >
            {/* Down arrow SVG */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <path d="M5 8l7 8 7-8" />
            </svg>
          </button>
        </div>
        </div>
      )}

      <div className="mb-2">
        <span className="font-semibold text-gray-800 dark:text-gray-100">Players:</span>
        <ul className="inline-block ml-2">
          {gamePlayers.map((p) => (
            <li
              key={p.socketId}
              className={
                "inline-block mr-3 " +
                (winners.includes(p.socketId)
                  ? "text-blue-400 dark:text-blue-400 font-bold"
                  : "text-gray-800 dark:text-gray-100")
              }
            >
              {p.username}: {scores[p.socketId] || 0}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

SnakeGame.Guide = function SnakeGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Snake</h3>
      <ul className="list-disc ml-6">
        <li>Control your snake to eat food and grow longer.</li>
        <li>Avoid crashing into walls or other snakes.</li>
        <li>Last snake alive wins!</li>
      </ul>
    </div>
  );
};

export default SnakeGame;
